import { addHook, sanitize, clearWindow, removeAllHooks } from 'isomorphic-dompurify';

/**
 * Allowed HTML tags.
 * These correspond to the tags that RichText component handles
 * via its replacement system (Sparky components).
 */
const ALLOWED_TAGS = ['strong', 'em', 'a', 'br', 'p', 'ul', 'ol', 'li', 'span', 'button', 'h3', 'h4', 'h5', 'h6'];

const ALLOWED_ATTR = ['href', 'target', 'rel', 'data-chattopic', 'data-unwrappable', 'class'];

/**
 * Sanitizes HTML so it can be safely rendered in the UI.
 * Keeps only a strict allowlist of formatting tags/attributes and removes
 * potentially dangerous markup (for example scripts, iframes, event handlers).
 *
 * @param dirty - Unsafe HTML input
 * @returns Sanitized HTML string safe to render
 */
export function sanitizeHtml(dirty: string): string {
  try {
    addHook('afterSanitizeAttributes', (node: Element) => {
      if (node.tagName === 'A' && node.getAttribute('target') === '_blank') {
        // Prevent reverse tabnabbing for links with target="_blank"
        const existingRel = node.getAttribute('rel');
        const relValues = existingRel ? existingRel.split(/\s+/) : [];
        if (!relValues.includes('noopener')) {
          relValues.push('noopener');
        }
        if (!relValues.includes('noreferrer')) {
          relValues.push('noreferrer');
        }
        node.setAttribute('rel', relValues.join(' '));
      }
    });

    return sanitize(dirty, {
      ALLOWED_TAGS,
      ALLOWED_ATTR,
    });
  } finally {
    clearWindow(); // SSR: Releases jsdom resources and clears hooks; Browser: no-op
    removeAllHooks(); // Remove any existing hooks to avoid duplicates in browser
  }
}
/**
 * Strip all HTML tags from text, returning plain text only.
 * Used for sanitizing user input before sending to the backend.
 *
 * @param dirty - Unsafe HTML input
 * @returns Plain text with all HTML tags removed
 */
export function stripHtmlTags(dirty: string): string {
  try {
    return sanitize(dirty, { ALLOWED_TAGS: [], ALLOWED_ATTR: [] });
  } finally {
    clearWindow(); // SSR: Releases jsdom resources; Browser: no-op
  }
}
